import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Loader, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';

const AuthCallback: React.FC = () => {
  const { checkAuthStatus, isAuthenticated, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const processCallback = async () => {
      try {
        const errorParam = searchParams.get('error');
        const token = searchParams.get('token');
        const refreshToken = searchParams.get('refreshToken');

        console.log('🔍 Processing OAuth callback...');
        console.log('Error param:', errorParam);
        console.log('Token present:', !!token);

        if (errorParam) {
          setError(errorParam === 'google_auth_failed' 
            ? 'Error en la autenticación con Google' 
            : 'Error de autenticación OAuth');
          setProcessing(false);
          return;
        }

        if (!token) {
          setError('No se recibió token de autenticación');
          setProcessing(false);
          return;
        }

        // Store tokens in localStorage
        localStorage.setItem('token', token);
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }

        // Fetch user data with the new token
        await checkAuthStatus();
        setSuccess(true);
        setProcessing(false);

        // Redirect after a brief delay
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1500);

      } catch (error) {
        console.error('Error processing OAuth callback:', error);
        setError(error instanceof Error ? error.message : 'Error de autenticación');
        setProcessing(false);
      }
    };

    processCallback();
  }, [searchParams, checkAuthStatus]);

  // Mostrar estado de carga
  if (processing || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="mb-4">
            <Logo size="md" variant="dark" />
          </div>
          <Loader className="h-8 w-8 animate-spin text-dental-600" />
          <p className="mt-2 text-gray-600">Procesando autenticación...</p>
          <p className="mt-1 text-sm text-gray-500">Esto puede tomar unos segundos</p>
        </div>
      </div>
    );
  }

  // Mostrar éxito
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center">
          <div className="mb-4">
            <Logo size="md" variant="dark" />
          </div>
          <CheckCircle className="h-12 w-12 text-green-600" />
          <p className="mt-2 text-gray-800 font-medium">¡Autenticación exitosa!</p>
          <p className="mt-1 text-sm text-gray-600">Redirigiendo al panel principal...</p>
        </div>
      </div>
    );
  }

  // Mostrar error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center max-w-md text-center">
          <div className="mb-4">
            <Logo size="md" variant="dark" />
          </div>
          <XCircle className="h-12 w-12 text-red-600" />
          <p className="mt-2 text-gray-800 font-medium">Error de autenticación</p>
          <p className="mt-1 text-sm text-gray-600">{error}</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="mt-4 px-4 py-2 bg-dental-600 text-white rounded-md hover:bg-dental-700"
          >
            Volver al login
          </button>
        </div>
      </div>
    );
  }

  // Redirigir si ya está autenticado
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Fallback a login
  return <Navigate to="/login?error=auth_failed" replace />;
};

export default AuthCallback;